import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollText, Loader2 } from "lucide-react";

// Blocks the app for a logged-in user whose accepted Terms version is out of date.
export default function TermsGate() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || user.terms_current !== false) return null;

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/accept-terms");
      setUser(data.user);
      toast.success("Thanks — you're all set.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't record your acceptance");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        data-testid="terms-gate-modal"
        className="max-w-md [&>button.absolute]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ScrollText className="h-5 w-5" />
          </div>
          <DialogTitle className="text-center mt-2">We've updated our Terms</DialogTitle>
          <DialogDescription className="text-center">
            We've made changes to our{" "}
            <Link to="/terms" target="_blank" data-testid="terms-gate-terms-link" className="font-semibold text-primary hover:underline">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" target="_blank" data-testid="terms-gate-privacy-link" className="font-semibold text-primary hover:underline">Privacy Policy</Link>.
            Please review and accept them to continue using BogoShare.
          </DialogDescription>
        </DialogHeader>
        <Button data-testid="terms-gate-accept-btn" onClick={accept} disabled={busy}
          className="w-full h-11 rounded-full mt-2">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          I agree to the updated Terms
        </Button>
      </DialogContent>
    </Dialog>
  );
}
